import { Country, CountryCode, MarketConfig, ShopifyCountry, StoreKey } from '~/types'
import { useSelector } from './hooks'

export function getStoreCountry(): StoreKey {
	switch (window.Shopify.shop) {
		case 'auk-us.myshopify.com':
			return 'US'
		case 'auk-sweden.myshopify.com':
			return 'SE'
		case 'aukstore.myshopify.com':
			return 'NO'
		case 'aukeco.myshopify.com':
			return 'EU'
	}
}
export function requiresStoreSwitch(current: CountryCode | 'EU', next: CountryCode): boolean {
	if (current === next) return false
	const store = getStoreCountry()
	if (store === 'US' && next !== CountryCode.US) return true
	if (store === 'SE' && next !== CountryCode.SE) return true
	if (store === 'NO' && next !== CountryCode.NO) return true
	if (getStoreCountry() === 'EU' && [CountryCode.NO, CountryCode.SE, CountryCode.US].includes(next)) return true
	return false
}

// eslint-disable-next-line no-shadow -- TODO: This rule should be disabled as it's bugged for TypeScript
export enum MarketSwitchStatus {
	None = 0,
	Domain = 'Domain',
	Currency = 'Currency',
	Store = 'Store'
}

export function requiresMarketSwitch(
	markets: MarketConfig[],
	current: CountryCode | 'EU',
	next: CountryCode
): MarketSwitchStatus {
	return getMarketSwitchConfig(markets, current, next).status
}

type MarketSwitchConfig =
	| { status: MarketSwitchStatus.None; nextMarket: null }
	| { status: Exclude<MarketSwitchStatus, MarketSwitchStatus.None>; nextMarket: MarketConfig }

export function getMarketSwitchConfig(
	markets: MarketConfig[],
	current: CountryCode | 'EU',
	next: CountryCode
): MarketSwitchConfig {
	if (current === next) return { status: MarketSwitchStatus.None, nextMarket: null }
	const nextMarket = markets.find(m => m.countries.includes(next))
	const currentMarket = markets.find(m => (current === 'EU' ? m.store === 'EU' : m.countries.includes(current)))
	if (!nextMarket || !currentMarket) return { status: MarketSwitchStatus.None, nextMarket: null }
	if (currentMarket.id === nextMarket.id) return { status: MarketSwitchStatus.None, nextMarket: null }
	if (nextMarket.store !== currentMarket.store) return { status: MarketSwitchStatus.Store, nextMarket }
	if (nextMarket.currency !== currentMarket.currency) return { status: MarketSwitchStatus.Currency, nextMarket }
	if (nextMarket.webPresences[0].domain !== currentMarket.webPresences[0].domain)
		return { status: MarketSwitchStatus.Domain, nextMarket }
	return { status: MarketSwitchStatus.None, nextMarket: null }
}

export function getPrefferedCountryCode(): Country['iso_code'] {
	const cookie = document.cookie
		.split('; ')
		.find(ck => ck.startsWith('localization'))
		?.split('=')
	const cookieCode = cookie ? cookie[1] : null
	const storageCode = localStorage.getItem('preferredCountryCode')

	let country: string | null = null
	if (getStoreCountry() === 'EU') country = storageCode ?? cookieCode
	else country = cookieCode ?? storageCode
	return (country as Country['iso_code']) ?? 'EU'
}

export function getPrefferedCountry(): ShopifyCountry | null {
	const code = getPrefferedCountryCode()
	return window.localization.available_countries.find(c => c.iso_code === code) ?? null
}

export function getCurrentCountry() {
	return getPrefferedCountry() ?? window.localization.country
}

export function getDefaultCountry(): Country {
	// TODO: Remove EU as default when country selection is working
	const EU: Country = {
		...(window.localization?.country ?? {}),
		iso_code: 'EU',
		name: 'Europe'
	}
	return getPrefferedCountry() ?? EU ?? window.localization.country
}

export function getCurrentLanguage() {
	return window.localization.language.shop_locale
}

export function useCurrentCountryCode(): Country['iso_code'] {
	const cartCountry = useSelector(s => s.cart.buyerIdentity?.countryCode)
	const currentCountry = getPrefferedCountryCode()
	return cartCountry ?? currentCountry
}
